// frontend/src/components/marketplace/QuoteRequestForm.tsx
// Request quote form for products requiring custom pricing

import React, { useState } from 'react';
import {
  FileText,
  User,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Package,
  MessageSquare,
  CheckCircle,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import type { QuoteRequest, Product } from '../types/marketplace';

interface QuoteRequestFormProps {
  product: Product;
  vendorName: string;
  onSubmit: (data: Omit<QuoteRequest, 'productId'>) => Promise<void>;
  onCancel?: () => void;
  className?: string;
}

interface FormData {
  name: string;
  email: string;
  phone: string;
  childAge: string;
  childDiagnosis: string;
  deliveryZipCode: string;
  quantity: number;
  urgency: 'standard' | 'urgent' | 'flexible';
  insuranceInfo: string;
  additionalRequirements: string;
  preferredContactMethod: 'email' | 'phone' | 'either';
  bestTimeToContact: string;
}

interface FormErrors {
  [key: string]: string;
}

export const QuoteRequestForm: React.FC<QuoteRequestFormProps> = ({
  product,
  vendorName,
  onSubmit,
  onCancel,
  className = '',
}) => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    childAge: '',
    childDiagnosis: '',
    deliveryZipCode: '',
    quantity: 1,
    urgency: 'standard',
    insuranceInfo: '',
    additionalRequirements: '',
    preferredContactMethod: 'email',
    bestTimeToContact: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }

    if (!formData.deliveryZipCode.trim()) {
      newErrors.deliveryZipCode = 'Delivery ZIP code is required';
    } else if (!/^\d{5}(-\d{4})?$/.test(formData.deliveryZipCode)) {
      newErrors.deliveryZipCode = 'Please enter a valid ZIP code';
    }

    if (formData.quantity < 1) {
      newErrors.quantity = 'Quantity must be at least 1';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'number' ? parseInt(value) || 0 : value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      await onSubmit({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: buildMessage(),
      });
      setIsSuccess(true);
    } catch (error) {
      setErrors({ submit: 'Failed to submit quote request. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const buildMessage = (): string => {
    const parts = [
      `Product: ${product.name}`,
      `Quantity: ${formData.quantity}`,
      `Urgency: ${formData.urgency}`,
      formData.childAge && `Child's Age: ${formData.childAge}`,
      formData.childDiagnosis && `Diagnosis: ${formData.childDiagnosis}`,
      `Delivery ZIP: ${formData.deliveryZipCode}`,
      formData.insuranceInfo && `Insurance Info: ${formData.insuranceInfo}`,
      formData.bestTimeToContact && `Best time to contact: ${formData.bestTimeToContact}`,
      formData.additionalRequirements && `Additional Requirements:\n${formData.additionalRequirements}`,
    ].filter(Boolean);

    return parts.join('\n');
  };

  if (isSuccess) {
    return (
      <div className={`bg-white rounded-xl p-8 text-center ${className}`}>
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="font-display text-xl font-semibold text-navy mb-2">
          Quote Request Submitted!
        </h3>
        <p className="text-warm-gray mb-6">
          {vendorName} will review your request and provide a customized quote within 1-2 business days.
          Check your email at <strong className="text-navy">{formData.email}</strong> for updates.
        </p>
        <div className="bg-cream rounded-lg p-4 mb-6">
          <p className="text-sm text-navy">
            <strong>What's next?</strong>
          </p>
          <ul className="text-sm text-warm-gray mt-2 space-y-1">
            <li>• The vendor will review your specific requirements</li>
            <li>• They may contact you for additional details</li>
            <li>• You'll receive a detailed quote by email</li>
          </ul>
        </div>
        {onCancel && (
          <button
            onClick={onCancel}
            className="text-sage hover:text-sage-dark font-medium"
          >
            Return to Product
          </button>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className={`bg-white rounded-xl ${className}`}>
      {/* Header */}
      <div className="p-6 border-b border-gray-100">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-terracotta/10 rounded-lg flex items-center justify-center flex-shrink-0">
            <FileText className="w-6 h-6 text-terracotta" />
          </div>
          <div>
            <h3 className="font-display text-lg font-semibold text-navy">
              Request a Quote
            </h3>
            <p className="text-sm text-warm-gray mt-1">
              Get customized pricing for <strong>{product.name}</strong> from {vendorName}
            </p>
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className="p-6 space-y-6">
        {/* Contact Information */}
        <div>
          <h4 className="text-sm font-semibold text-navy mb-3">Contact Information</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-navy mb-1">
                Your Name *
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-warm-gray" />
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full pl-10 pr-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-sage/20 focus:border-sage transition-colors ${
                    errors.name ? 'border-red-300' : 'border-gray-200'
                  }`}
                  placeholder="Full name"
                />
              </div>
              {errors.name && (
                <p className="text-red-500 text-xs mt-1">{errors.name}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-navy mb-1">
                Email Address *
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-warm-gray" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full pl-10 pr-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-sage/20 focus:border-sage transition-colors ${
                    errors.email ? 'border-red-300' : 'border-gray-200'
                  }`}
                  placeholder="your@email.com"
                />
              </div>
              {errors.email && (
                <p className="text-red-500 text-xs mt-1">{errors.email}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-navy mb-1">
                Phone Number *
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-warm-gray" />
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className={`w-full pl-10 pr-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-sage/20 focus:border-sage transition-colors ${
                    errors.phone ? 'border-red-300' : 'border-gray-200'
                  }`}
                  placeholder="(555) 123-4567"
                />
              </div>
              {errors.phone && (
                <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-navy mb-1">
                Preferred Contact Method
              </label>
              <select
                name="preferredContactMethod"
                value={formData.preferredContactMethod}
                onChange={handleChange}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-sage/20 focus:border-sage transition-colors"
              >
                <option value="email">Email</option>
                <option value="phone">Phone</option>
                <option value="either">Either is fine</option>
              </select>
            </div>
          </div>
        </div>

        {/* Product Details */}
        <div>
          <h4 className="text-sm font-semibold text-navy mb-3">Product Details</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-navy mb-1">
                Quantity *
              </label>
              <div className="relative">
                <Package className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-warm-gray" />
                <input
                  type="number"
                  name="quantity"
                  value={formData.quantity}
                  onChange={handleChange}
                  min={1}
                  className={`w-full pl-10 pr-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-sage/20 focus:border-sage transition-colors ${
                    errors.quantity ? 'border-red-300' : 'border-gray-200'
                  }`}
                />
              </div>
              {errors.quantity && (
                <p className="text-red-500 text-xs mt-1">{errors.quantity}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-navy mb-1">
                Delivery ZIP Code *
              </label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-warm-gray" />
                <input
                  type="text"
                  name="deliveryZipCode"
                  value={formData.deliveryZipCode}
                  onChange={handleChange}
                  className={`w-full pl-10 pr-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-sage/20 focus:border-sage transition-colors ${
                    errors.deliveryZipCode ? 'border-red-300' : 'border-gray-200'
                  }`}
                  placeholder="12345"
                  maxLength={10}
                />
              </div>
              {errors.deliveryZipCode && (
                <p className="text-red-500 text-xs mt-1">{errors.deliveryZipCode}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-navy mb-1">
                Urgency
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-warm-gray" />
                <select
                  name="urgency"
                  value={formData.urgency}
                  onChange={handleChange}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-sage/20 focus:border-sage transition-colors appearance-none"
                >
                  <option value="flexible">Flexible timing</option>
                  <option value="standard">Standard (2-4 weeks)</option>
                  <option value="urgent">Urgent (ASAP)</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Child Information (Optional) */}
        <div>
          <h4 className="text-sm font-semibold text-navy mb-1">Child Information</h4>
          <p className="text-xs text-warm-gray mb-3">
            Optional - helps the vendor provide more accurate sizing/recommendations
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-navy mb-1">
                Child's Age
              </label>
              <input
                type="text"
                name="childAge"
                value={formData.childAge}
                onChange={handleChange}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-sage/20 focus:border-sage transition-colors"
                placeholder="e.g., 5 years old"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-navy mb-1">
                Diagnosis/Condition
              </label>
              <input
                type="text"
                name="childDiagnosis"
                value={formData.childDiagnosis}
                onChange={handleChange}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-sage/20 focus:border-sage transition-colors"
                placeholder="e.g., Cerebral Palsy"
              />
            </div>
          </div>
        </div>

        {/* Insurance */}
        <div>
          <label className="block text-sm font-medium text-navy mb-1">
            Insurance Information
          </label>
          <p className="text-xs text-warm-gray mb-2">
            If applicable, provide your insurance details for coverage verification
          </p>
          <input
            type="text"
            name="insuranceInfo"
            value={formData.insuranceInfo}
            onChange={handleChange}
            className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-sage/20 focus:border-sage transition-colors"
            placeholder="e.g., Blue Cross Blue Shield PPO"
          />
        </div>

        {/* Additional Requirements */}
        <div>
          <label className="block text-sm font-medium text-navy mb-1">
            Additional Requirements or Questions
          </label>
          <div className="relative">
            <MessageSquare className="absolute left-3 top-3 w-4 h-4 text-warm-gray" />
            <textarea
              name="additionalRequirements"
              value={formData.additionalRequirements}
              onChange={handleChange}
              rows={4}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-sage/20 focus:border-sage transition-colors resize-none"
              placeholder="Any specific requirements, customizations, or questions about this product..."
            />
          </div>
        </div>

        {/* Best Time to Contact */}
        <div>
          <label className="block text-sm font-medium text-navy mb-1">
            Best Time to Contact
          </label>
          <input
            type="text"
            name="bestTimeToContact"
            value={formData.bestTimeToContact}
            onChange={handleChange}
            className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-sage/20 focus:border-sage transition-colors"
            placeholder="e.g., Weekdays after 3pm EST"
          />
        </div>

        {/* Error Message */}
        {errors.submit && (
          <div className="flex items-center gap-2 p-3 bg-red-50 text-red-600 rounded-lg">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <p className="text-sm">{errors.submit}</p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-6 py-4 bg-cream-dark/30 border-t border-gray-100 flex items-center justify-between gap-4">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-warm-gray hover:text-navy transition-colors"
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={isSubmitting}
          className="flex items-center gap-2 px-6 py-2.5 bg-terracotta text-white rounded-lg hover:bg-terracotta/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ml-auto"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Submitting...
            </>
          ) : (
            <>
              <FileText className="w-4 h-4" />
              Request Quote
            </>
          )}
        </button>
      </div>
    </form>
  );
};

export default QuoteRequestForm;
